import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { BackIcon } from '../components/icons.js';
import { DOCUMENT_KINDS, workbenchKindFor, type Output } from '@jabberr/core';
import { EmptyState } from '../components/EmptyState.js';
import { UnlinkedNote, WorkbenchSave } from '../components/WorkbenchSave.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { conversationTitle, relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';

/** Requirements drafts the product owner wrote in this project, newest first. Read, copy, carry on elsewhere. */
export function OutputsPage() {
  const { projectId = '' } = useParams();
  // setInfo alone: the drawer object changes whenever its info does, so depending on it re-ran this effect forever.
  const { setInfo } = useDrawer();
  const [outputs, setOutputs] = useState<Output[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Save to the Workbench from the list: offered when this Jabberr talks to Anchor Yard (canLinkAnchor, the same
  // settings the save needs) and the person is not a guest; the server still decides on every save.
  const [canSave, setCanSave] = useState(false);
  const [linked, setLinked] = useState<boolean | null>(null);

  useEffect(() => {
    api.projects
      .get(projectId)
      .then((r) => {
        setCanSave(r.canLinkAnchor === true);
        setLinked(Boolean(r.project.workbenchProjectId));
      })
      .catch(() => {
        setCanSave(false);
        setLinked(null);
      });
  }, [projectId]);

  useEffect(() => {
    setOutputs(null);
    setInfo({ projectId, title: 'Saved documents' });
    api.outputs
      .list(projectId)
      .then((r) => setOutputs(r.outputs))
      .catch((err) => setError(errorMessage(err)));
  }, [projectId, setInfo]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <Link to={`/p/${projectId}/knows`} className="back-link">
          <BackIcon />
          What Jabberr knows
        </Link>
          <h1>Saved documents</h1>
        </div>
      </header>
      <p className="page-subtitle muted small">
        Documents Jabberr wrote when asked (Write... in a conversation) and replies people chose to keep. Read them, copy them, or save a
        requirements draft, brief or decision memo to the Anchor Workbench, where work starts.
      </p>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {outputs === null ? (
        <Spinner label="Loading" />
      ) : outputs.length === 0 ? (
        <EmptyState title="Nothing saved yet." />
      ) : (
        <>
          {canSave && linked === false && outputs.some((o) => workbenchKindFor(o.purpose) && !o.workbench) ? <UnlinkedNote projectId={projectId} /> : null}
          <ul className="list output-list">
            {outputs.map((o) => (
              <li key={o.turnId} className="list-item output-row">
                <Link to={`/p/${projectId}/outputs/${o.turnId}`} className="list-main">
                  <span className="list-title">{o.preview || 'Untitled'}</span>
                  <span className="muted small">
                    {o.purpose === 'saved' ? 'kept reply' : DOCUMENT_KINDS[o.purpose].label.toLowerCase()} · from {conversationTitle(o.conversationTitle)} · {relativeTime(o.createdAt)}
                  </span>
                </Link>
                <WorkbenchSave
                  projectId={projectId}
                  turnId={o.turnId}
                  workbench={o.workbench}
                  canSave={canSave && workbenchKindFor(o.purpose) !== null}
                  linked={linked}
                  primary={false}
                  hideNote
                  onSaved={(workbench) => setOutputs((prev) => prev?.map((x) => (x.turnId === o.turnId ? { ...x, workbench } : x)) ?? prev)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
